import { AXON_REGION, VERSION } from '@env';
import { forkJoin, Subject, throwError } from 'rxjs';
import { catchError, delay, finalize, retryWhen, take, takeUntil, tap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { SnChangePasswordDialogComponent } from '@lib/account/components/change-password/change-password.dialog';
import { SN_API_URL } from '@lib/api';
import { SnAuditService } from '@lib/api/services';
import {
  ISnAuthConfig,
  ObtainResponse,
  SnCurrentOrganization,
  SnCurrentUser,
  SnSessionService,
} from '@lib/auth';
import { PublicOrganization } from '@lib/auth/public';
import { snFadeInDown } from '@lib/common/animations';
import { t } from '@lib/translation/helpers';
import { LanguageSelectionService, TranslationService } from '@lib/translation/services';
import { SnDialogService } from '@lib/ui/dialog';

import { ISignInFormModel } from '../../components/sign-in-form';
import { TwoFactorAuthDialogComponent } from '../../components/two-factor-auth-dialog';
import { SN_AUTH_CONFIG } from '../../providers';
import { AxonDisclaimerComponent } from '../axon-disclaimer';
import { CjisComplianceComponent } from '../cjis-compliance';
import { EcomAssociationComponent } from '../ecom-association';

@Component({
  selector: 'sn-sign-in',
  templateUrl: './sign-in.view.html',
  styleUrls: ['./sign-in.view.scss'],
  providers: [SnCurrentOrganization],
  animations: [snFadeInDown],
})
export class SignInView implements OnInit, OnDestroy {
  public readonly signinAllowed: boolean;
  public readonly form: UntypedFormGroup;
  public readonly formModel: ISignInFormModel;
  public showingPassword: boolean = false;
  public buttonMessage: string = 'common.next';
  public isRedirecting: boolean;

  public readonly signUpEnabled: boolean;
  @HostBinding('class.container') public readonly isRegistry: boolean;
  public readonly isVideoMeet: boolean;

  public isLoading: boolean = false;
  public version: string = VERSION;
  public backendVersion: string = localStorage.getItem('backendVersion');
  public axonSsoUrl: URL;

  private readonly _redirectUrl: string;
  private readonly _defaultRedirect: string;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    public activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _session: SnSessionService,
    private readonly _snackbar: MatSnackBar,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _matDialog: MatDialog,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _currentUser: SnCurrentUser,
    private readonly _dialogService: SnDialogService,
    private readonly _auditService: SnAuditService,
    private readonly _formBuilder: UntypedFormBuilder,
    @Optional() private readonly _translationService: TranslationService,
    @Optional() private readonly _languageSelection: LanguageSelectionService,
    @Inject(SN_AUTH_CONFIG) authConfig: ISnAuthConfig,
    @Optional() public readonly organization: PublicOrganization,
    @Inject(SN_API_URL) private readonly _apiUrl: string,
  ) {
    this._defaultRedirect = authConfig.redirectAuthorizedTo;
    const queryParams = activatedRoute.snapshot.queryParams;
    this.signinAllowed = this.organization?.allowRegistry || !authConfig.isRegistry;

    if (authConfig.isRegistry) {
      if (this.organization.registryRedirectUrl) {
        window.location.href = this.organization.registryRedirectUrl;
      }
    }
    let emailTrim = queryParams.email ? queryParams.email.trim() : '';
    emailTrim = emailTrim.replace(' ', '+');

    if (queryParams.pw) {
      this.showingPassword = true;
      this.formModel = {
        email: emailTrim,
        password: queryParams.pw,
      };
      this.form = this._formBuilder.group({
        email: [emailTrim, [Validators.required, Validators.email]],
        password: [queryParams.pw, [Validators.required]],
      });
      this.signUpEnabled = authConfig.signUpEnabled;
      this.isRegistry = authConfig.isRegistry;
      this._redirectUrl = queryParams.redirectUrl || authConfig.redirectAuthorizedTo;
      setTimeout(() => {
        this.onFormSubmit();
      }, 500);
    } else {
      this.formModel = {
        email: emailTrim,
      };
      this.form = this._formBuilder.group({
        email: [emailTrim, [Validators.required, Validators.email]],
        password: ['', [Validators.required]],
      });
      this.signUpEnabled = authConfig.signUpEnabled;
      this.isRegistry = authConfig.isRegistry;
      this.isVideoMeet = authConfig.isRegistry;
      this._redirectUrl = queryParams.redirectUrl || authConfig.redirectAuthorizedTo;
    }

    this.axonSsoUrl = new URL('/api/axon/sso/login', this._apiUrl);
    this.axonSsoUrl.searchParams.append('region', AXON_REGION);
    if (this._redirectUrl) {
      this.axonSsoUrl.searchParams.append('redirect_uri', this._redirectUrl);
    }
  }

  get showPassword(): boolean {
    return this.showingPassword;
  }

  public ngOnInit(): void {
    if (this._languageSelection) {
      this._languageSelection
        .initialize(this._currentUser)
        .pipe(takeUntil(this._destroy$))
        .subscribe();
    } else if (this._translationService) {
      this._translationService.initialize('en-US');
    }
  }

  public checkEmail(): void {
    const email = this.form.value.email;
    const emailControl = this.form.get('email');

    if (!email || emailControl?.invalid || !this.isValidEmail(email)) {
      return;
    }

    const app = this.activatedRoute.parent.snapshot.params.app || 'ops';
    const test = this.activatedRoute.parent.snapshot.params.isTest || false;
    this._session
      .providersByEmail(email, app, test)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          this.buttonMessage = 'common.signIn';
          let orgPrefix;
          if (result?.results.length > 0) {
            orgPrefix = result.results[0].orgPrefix;
          }
          if (orgPrefix) {
            this._router.navigate([`/sso/${orgPrefix}`]);
          } else {
            this.showingPassword = true;
            this.form.addControl('password', this._formBuilder.control('', [Validators.required]));
            this._cdr.detectChanges();
            const passwordField = document.querySelector<HTMLElement>('.password-field');
            if (passwordField) {
              passwordField.focus();
            }
          }
        },
        error: (err) => {
          switch (err.status) {
            case 400:
              this.form.setErrors(err.error);
              break;
            case 404:
              this.form.setErrors({
                nonFieldErrors: ['This user account does not exist. Please create an account.'],
              });
              break;
          }
        },
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      return;
    }
    this.isLoading = true;
    this._cdr.markForCheck();

    this._session
      .create(this.form.value.email, this.form.value.password)
      .pipe(
        tap((response) => this._setUserAndToken(response)),
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
      )
      .subscribe({
        next: this._onSuccess.bind(this),
        error: this._onError.bind(this),
      });
  }

  public getErrors(): string {
    let errors = '';
    Object.keys(this.form.errors).forEach((key) => {
      this.form.errors[key].forEach((error) => {
        errors += `${error}\r\n`;
      });
    });
    return errors;
  }

  private _onSuccess(data: any): void {
    if (data.mfaEnabled || data.twoFactorAuthRequired) {
      this.isRedirecting = true;
      this.buttonMessage = 'auth.signIn.completeTwoFactorAuth';
      this._matDialog
        .open(TwoFactorAuthDialogComponent, {
          data,
          disableClose: true,
        })
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((result) => {
          if (!result) {
            this.isRedirecting = false;
            this.buttonMessage = 'common.signIn';
            return;
          }
          this.isRedirecting = true;
          this.buttonMessage = 'auth.signIn.loginSuccessful';
          this._getUserProfile();
        });
    } else {
      this.isRedirecting = true;
      this.buttonMessage = 'auth.signIn.loginSuccessful';
      this._getUserProfile();
    }
  }

  private _setUserAndToken(response: ObtainResponse): void {
    if (response?.mfaEnabled || response?.twoFactorAuthRequired) {
      return;
    }
    this._currentUser.mfaEnabled = response?.mfaEnabled ?? false;
    this._session.setToken(response.token, response.ablyToken, response.azureWebpubsubUrl);
  }

  private _getUserProfile(): void {
    forkJoin([this._currentUser.getProfile(), this._currentOrganization.getProfile()])
      .pipe(
        catchError((err) => {
          return throwError(err);
        }),
        retryWhen((err) => {
          return err.pipe(delay(3000), take(3));
        }),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        if (!this._currentOrganization.settings.isCompliance) {
          this._session.setCjisApprovedKey();
        }

        if (!!this._currentUser.passwordDaysLeft && this._currentUser.passwordDaysLeft <= 14) {
          this._openPasswordExpirationDialog();
        } else {
          this._checkEcomAssociation();
        }
      });
  }

  private _openPasswordExpirationDialog(): void {
    this._dialogService
      .info(
        {
          message: t('auth.signIn.passwordExpiration.message', {
            daysLeft: this._currentUser.passwordDaysLeft,
          }),
          confirmButton: {
            text: t('common.reset'),
            color: 'success',
          },
          cancelButton: {
            text: t('auth.signIn.passwordExpiration.notNowButton'),
            color: 'warn',
          },
        },
        'fs__updatePasswordDialog',
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (confirm) {
          this._changePassword();
        } else {
          this._checkCJISCompliance();
        }
      });
  }

  private _changePassword(): void {
    const dialogRef = this._matDialog.open(SnChangePasswordDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._checkEcomAssociation();
      });
  }

  private _checkCJISCompliance(): void {
    if (this._currentOrganization.settings.isCompliance) {
      this._openCJISDialog();
      return;
    }

    this._openAxonDisclaimerDialog();
  }

  private _checkEcomAssociation(): void {
    const modalEnabled = this._currentOrganization.settings.ecomIntegrationUseNewUserAttribution;
    const userIsAssociated = !!this._currentUser.axonUserId;
    const showModal = modalEnabled && !userIsAssociated;
    if (showModal) {
      return this._openEcomAssociationDialog();
    }

    // User doesn't need the modal - continue to CJIS compliance check
    this._checkCJISCompliance();
  }

  private _openCJISDialog(): void {
    const dialog = this._matDialog.open(CjisComplianceComponent, {
      data: {
        message: this._currentOrganization.settings.complianceMessage,
        title: this._currentOrganization.settings.complianceTitle,
      },
      panelClass: 'my-custom-dialog-class',
      disableClose: true,
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (!confirm) {
          this.buttonMessage = 'common.signIn';
          this.isRedirecting = false;
          return;
        }
        this._session.setCjisApprovedKey();
        this._auditService
          .createDisclaimerAudit({
            disclaimer: 'CJIS',
            userId: this._currentUser.id,
            userName: this._currentUser.fullName,
          })
          .subscribe();
        this._openAxonDisclaimerDialog();
      });
  }

  private _openEcomAssociationDialog(): void {
    const dialog = this._matDialog.open(EcomAssociationComponent, {
      panelClass: 'ecom-association-dialog',
      disableClose: true,
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (!confirm) {
          this.buttonMessage = 'Sign In';
          this.isRedirecting = false;
          return;
        }

        // User successfully associated with Ecom - continue to CJIS compliance check
        this._checkCJISCompliance();
      });
  }

  private _openAxonDisclaimerDialog(): void {
    if (this._session.isAxonSso && this._currentOrganization.settings.axonWarningEnabled) {
      const dialog = this._matDialog.open(AxonDisclaimerComponent, {
        data: {
          message: this._currentOrganization.settings.axonWarningDescription,
          title: this._currentOrganization.settings.axonWarningTitle,
        },
        panelClass: 'axon-disclaimer-dialog-class',
        disableClose: true,
      });
      dialog
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((confirm) => {
          if (confirm) {
            this._session.setCjisApprovedKey();
            this._auditService
              .createDisclaimerAudit({
                disclaimer: 'AXON',
                userId: this._currentUser.id,
                userName: this._currentUser.fullName,
              })
              .subscribe();
          } else {
            this._session.removeCjisApprovedKey();
          }

          this._router.navigateByUrl(this._getRedirectUrl());
        });

      return;
    }

    this._router.navigateByUrl(this._getRedirectUrl());
  }

  private _onError(errorResponse: HttpErrorResponse): void {
    switch (errorResponse.status) {
      case 401:
        this._router.navigateByUrl(
          `/auth/change/${errorResponse.error.uid}/${errorResponse.error.token}`,
        );
        break;
      default:
        this.form.setErrors(errorResponse.error);
        break;
    }
  }

  private _getRedirectUrl(): string {
    return this._redirectUrl.includes('auth/sign-in') ? this._defaultRedirect : this._redirectUrl;
  }
}
